/**
 * useCrumbs — computes the breadcrumb trail for the current location.
 * Shared by the topbar <Breadcrumbs>, the mobile compact back-crumb, and
 * the document-title effect so all three always agree.
 *
 * Two derivation modes:
 *
 * 1. JOURNEY — when the nav trail (lib/nav-context) attests a real path the
 *    user walked that the URL alone can't express (came from a different
 *    section, or hopped through another entity — e.g. Dashboard → Campaign
 *    → Keyword run). The crumbs replay that path, each hop clickable.
 *
 * 2. CANONICAL — deep links, refreshes, and plain Section → detail flows.
 *    Derived from the URL + registry, enriched with the owning project when
 *    the entity is in the query cache:
 *      Dashboard → Projects → <Project> → <Section> → <entity label>
 *
 * Entity crumbs use human labels from the registry's `entityLabel`
 * accessors (brief keyword, run seed, report period, …) and only fall back
 * to `…uuid6` while the entity is still loading. Crumb links are only ever
 * emitted for paths that exist in the route table — the registry knows the
 * real routes, so "auto"/"fires"-style 404 links can't happen.
 */
import { useEffect, useReducer } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import type { Project } from '@seo/types';
import { apiGet } from '@/lib/api';
import {
  matchDetail,
  sectionByPath,
  sectionByProjectTab,
  sectionLabelForPath,
  crumbLabelFor,
  CRUMB_QUERY_HEADS,
  SETTINGS_ITEMS,
  type CachedEntity,
  type DetailMatch,
} from '@/lib/nav-registry';
import { useNavTrail, type TrailEntry } from '@/lib/nav-context';

export interface BreadcrumbItem {
  label: string;
  /** Link target; omitted on the current (last) crumb and unroutable labels. */
  to?: string;
  /** Optional hash (e.g. a project tab) appended to `to`. */
  hash?: string;
}

const shortId = (id: string): string => `…${id.slice(0, 6)}`;

function entityCrumbLabel(qc: QueryClient, match: DetailMatch): string {
  const entity = qc.getQueryData<CachedEntity>(match.queryKey);
  if (entity) {
    const label = match.entityLabel(entity);
    if (label) return label;
  }
  return match.staticLabel ?? shortId(match.entityId);
}

/** Label a trail entry: entity label for detail hops, section label otherwise. */
function trailEntryLabel(qc: QueryClient, entry: TrailEntry): string {
  const detail = matchDetail(entry.path);
  if (detail) return entityCrumbLabel(qc, detail);
  return sectionLabelForPath(entry.path);
}

/**
 * Should the crumbs replay the journey instead of the canonical hierarchy?
 * Only when the trail ends on the current page AND carries information the
 * canonical derivation can't show.
 */
function journeyFor(
  trail: TrailEntry[],
  pathname: string,
): TrailEntry[] | null {
  if (trail.length < 2) return null;
  if (trail[trail.length - 1]!.path !== pathname) return null;
  const current = matchDetail(pathname);
  if (!current) return null;
  const priorHops = trail.slice(0, -1);
  const hasDetailHop = priorHops.some((e) => matchDetail(e.path) !== null);
  const rootDiffers = trail[0]!.path !== current.sectionPath;
  return hasDetailHop || rootDiffers ? trail : null;
}

export function useCrumbs(): BreadcrumbItem[] {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const trail = useNavTrail((s) => s.trail);
  const qc = useQueryClient();

  // Entity labels are read from the query cache with getQueryData, which is
  // NOT reactive — without this subscription the crumb tail would stay
  // "…uuid6" forever, because nothing re-renders the shell header when the
  // detail page's query resolves. Re-render on updates to the cache entries
  // we may read (entity + project heads only, so the shell isn't re-rendered
  // by unrelated list/polling queries).
  const [, bumpCacheTick] = useReducer((c: number) => c + 1, 0);
  useEffect(() => {
    // Cache events can fire synchronously while another component is
    // rendering (a mounting useQuery emits 'added' mid-render); defer +
    // coalesce the re-render so we never setState during someone else's
    // render phase.
    let scheduled = false;
    let disposed = false;
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' && event.type !== 'added') return;
      const head = event.query.queryKey[0];
      if (typeof head !== 'string' || !CRUMB_QUERY_HEADS.has(head)) return;
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        if (!disposed) bumpCacheTick();
      });
    });
    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [qc]);

  const detail = matchDetail(pathname);

  // Owning project for canonical enrichment. The detail page's own hook has
  // usually cached the entity already; we read project_id from it and fetch
  // the project on demand so deep links still get a named project crumb.
  const cachedEntity =
    detail && detail.kind !== 'project'
      ? qc.getQueryData<CachedEntity>(detail.queryKey)
      : null;
  const projectId =
    detail?.kind === 'project'
      ? detail.entityId
      : typeof cachedEntity?.project_id === 'string'
        ? cachedEntity.project_id
        : null;

  useQuery({
    queryKey: ['project', projectId],
    queryFn: () => apiGet<Project>(`/v1/projects/${projectId}`),
    enabled: !!projectId,
  });
  const project = projectId
    ? qc.getQueryData<Project>(['project', projectId]) ?? null
    : null;

  /* ---- 1. Journey mode ---- */
  const journey = journeyFor(trail, pathname);
  if (journey) {
    const crumbs: BreadcrumbItem[] = [];
    if (journey[0]!.path !== '/') crumbs.push({ label: 'Dashboard', to: '/' });
    for (let i = 0; i < journey.length; i++) {
      const entry = journey[i]!;
      const isLast = i === journey.length - 1;
      crumbs.push({
        label: trailEntryLabel(qc, entry),
        ...(isLast
          ? {}
          : { to: entry.path, ...(entry.hash ? { hash: entry.hash } : {}) }),
      });
      // The user walked through a project tab (e.g. Acme → Audits tab →
      // audit). Render that tab as its own level so the journey reads
      // "… → Acme → Audits → Audit …" instead of collapsing the section
      // into the project crumb.
      if (!isLast && entry.hash && matchDetail(entry.path)?.kind === 'project') {
        const tabSection = sectionByProjectTab(entry.hash);
        if (tabSection) {
          crumbs.push({
            label: crumbLabelFor(tabSection),
            to: entry.path,
            hash: entry.hash,
          });
        }
      }
    }
    return crumbs;
  }

  /* ---- 2. Canonical mode ---- */
  if (detail && detail.kind !== 'project') {
    const entityLabel = entityCrumbLabel(qc, detail);
    if (projectId) {
      const projectLabel = project?.name ?? shortId(projectId);
      return [
        { label: 'Dashboard', to: '/' },
        { label: 'Projects', to: '/projects' },
        { label: projectLabel, to: `/projects/${projectId}` },
        {
          label: detail.sectionLabel,
          to: `/projects/${projectId}`,
          ...(detail.projectTab ? { hash: detail.projectTab } : {}),
        },
        { label: entityLabel },
      ];
    }
    return [
      { label: 'Dashboard', to: '/' },
      { label: detail.sectionLabel, to: detail.sectionPath },
      { label: entityLabel },
    ];
  }

  if (detail && detail.kind === 'project') {
    const projectLabel = project?.name ?? shortId(detail.entityId);
    return [
      { label: 'Dashboard', to: '/' },
      { label: 'Projects', to: '/projects' },
      { label: projectLabel },
    ];
  }

  // Non-detail routes: URL segments with registry labels. Links are emitted
  // only for paths that actually route (sections, settings pages).
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: BreadcrumbItem[] = [{ label: 'Dashboard', to: '/' }];
  let acc = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    acc += `/${seg}`;
    const isLast = i === segments.length - 1;
    if (acc === '/settings') {
      // Bare /settings only redirects; link the crumb straight to Account.
      crumbs.push({ label: 'Settings', ...(isLast ? {} : { to: '/settings/account' }) });
      continue;
    }
    const section = sectionByPath(acc);
    const settingsItem = SETTINGS_ITEMS.find((s) => s.path === acc);
    const label = section
      ? crumbLabelFor(section)
      : settingsItem
        ? settingsItem.label
        : seg;
    const routable = !!section || !!settingsItem;
    crumbs.push({ label, ...(!isLast && routable ? { to: acc } : {}) });
  }
  return crumbs;
}

/**
 * Document title derived from the crumbs, tail-first:
 *   "crm software pricing · Briefs · Acme — SEO AI Agent"
 * Mount once in the authed shell (N-18).
 */
export function useDocumentTitle(crumbs: BreadcrumbItem[]): void {
  const labels = crumbs.map((c) => c.label);
  const title =
    labels.length <= 1
      ? 'Dashboard — SEO AI Agent'
      : `${labels
          .slice(1) // drop "Dashboard" when there's more context
          .reverse()
          .slice(0, 3)
          .join(' · ')} — SEO AI Agent`;

  useEffect(() => {
    document.title = title;
  }, [title]);
}

/** Static title for pages outside the shell (login/signup). */
export function useStaticTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} — SEO AI Agent`;
  }, [title]);
}
